import React, { useCallback, useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import cx from "classnames";
// import { Hooks } from "@xt/xtbase";
// const { useTranslation } = Hooks;
import store from "store";
import Socket from "utils/socket/public";
import { get_ticker24h } from "api/v4/market";
import { getFiatListByBalance } from "api/v4/fiat";
import { getAllowedFiatCodesForTabs, FIAT_MARKET_CODES } from "utils/getFiatCurrencyMap";

import useTimeout from "hooks/useTimeout";
import XtLoading from "components/xt/loading";
import XtScrollWindow from "components/xt/scroll/window";
import AppDivNoData from "components/app/div/noData";
import List from "./list";

import styles from "./index.module.scss";

import { TabCfgProps } from "../tabs";

import { TickerProps } from "store/trade";

interface Props {
  keyword: string;
  sortBy: string | undefined;
  tabCfg: TabCfgProps;
  isShowVolume: boolean;
  onMarketSelect?: () => void;
}

const WindowUl: React.FC<any> = ({ startIndex, record, isShowVolume, onMarketSelect }) => {
  // console.log("List====", { startIndex, record });
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    if (disabled)
      setTimeout(() => {
        setDisabled(false);
      }, 500);
  }, [disabled]);

  return record.map((item, index) => {
    return (
      <List
        key={`${item.s}-${startIndex + index}`}
        style={{ top: (startIndex + index) * 30 + "px" }}
        ticker={item}
        isShowVolume={isShowVolume}
        disabled={disabled}
        onClick={() => {
          setDisabled(true);
          onMarketSelect?.();
        }}
      />
    );
  });
};

const Main: React.FC<Props> = ({ keyword, sortBy, tabCfg, isShowVolume, onMarketSelect }) => {
  // const t = useTranslation();
  const router = useRouter();
  const { config } = store.market;
  const { tickers } = store.trade;
  const { isLogin, symbolStar } = store.user;

  // 检测当前是否在 CFD 页面
  const isCfd = useMemo(() => {
    return router.pathname?.startsWith("/cfd/") || router.asPath?.startsWith("/cfd/");
  }, [router.pathname, router.asPath]);

  // 检测当前是否是法币本位合约
  const isFiatContract = useMemo(() => {
    if (!isCfd) return false;
    const { name } = store.market;
    if (!name) return false;
    const quoteCurrency = name.split("_")[1]?.toUpperCase();
    const fiatCurrencies = ["AED", "BRL", "SAR"];
    return fiatCurrencies.includes(quoteCurrency);
  }, [isCfd, store.market.name]);

  // Crypto/Stocks/Commodities 下展示的法币对：按 账户充值法币、IP、余额 命中的法币过滤；都没命中则全展示
  const [allowedFiatCodes, setAllowedFiatCodes] = useState<string[] | null>(null);
  useEffect(() => {
    if (!isCfd) {
      setAllowedFiatCodes(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      let fiatCodesWithBalance: string[] = [];
      if (store.user.isLogin) {
        try {
          const balanceRes = await getFiatListByBalance({ params: { pageIndex: 1, pageSize: 100 }, isAutoPopError: false });
          const balanceData = balanceRes?.result ?? balanceRes?.data ?? balanceRes;
          const items = Array.isArray(balanceData) ? balanceData : balanceData?.items ?? balanceData?.list ?? [];
          items.forEach((item: any) => {
            const n = parseFloat(item.totalNum ?? item.totalAmount ?? item.availableAmount ?? 0);
            if (!Number.isNaN(n) && n > 0) {
              const code = (item.coinName ?? item.currency ?? "").toUpperCase();
              if (code && FIAT_MARKET_CODES.includes(code) && !fiatCodesWithBalance.includes(code)) {
                fiatCodesWithBalance.push(code);
              }
            }
          });
        } catch (_) {}
      }
      const codes = await getAllowedFiatCodesForTabs(store.user.userInfo?.fiatCurrencyIds, fiatCodesWithBalance);
      if (!cancelled) setAllowedFiatCodes(codes);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isCfd, store.user.userInfo?.fiatCurrencyIds, store.user.isLogin]);

  // const timeout = useRef<number>();
  // const timeoutApi = useRef<number>();
  const [loading, setLoading] = useState<boolean>(true);
  // const [resData, setResData] = useState<TickerProps[]>([]);

  const apiReqTicker24h = useCallback(
    (callback) => {
      if (!config) return;
      let resData: TickerProps[] = [];
      get_ticker24h()
        .then((data) => {
          resData = data;
        })
        .catch(() => {
          //empty
        })
        .finally(() => {
          const ary: TickerProps[] = [];
          let doc: TickerProps;
          for (const va in config) {
            doc = resData.find((obj) => obj.s === va) || { s: va };
            ary.push(doc);
          }
          // setResData(ary);
          // console.log("=======apiReqTicker24h updateState", ary);
          store.trade.updateState({ tickers: ary });
          callback && callback(ary);
        });
    },
    [config]
  );
  const [apiReqTicker24hLoop] = useTimeout(apiReqTicker24h, { ms: 5000, isLoop: true, isInitExec: true });
  // const apiReqTicker24hLoop = useCallback(() => {
  //   clearTimeout(timeoutApi.current);
  //   apiReqTicker24h((ary) => {
  //     setResData(ary);
  //   });
  //   timeoutApi.current = window.setTimeout(apiReqTicker24hLoop, 500);
  // }, [apiReqTicker24h]);

  // console.log("Socket.isReady =", Socket.isReady);
  const wsNotReadyApiLoop = useCallback(() => {
    // console.log("Socket.isReady -", Socket.isReady);
    !Socket.isReady && apiReqTicker24hLoop();
  }, [apiReqTicker24hLoop]);
  const [timeoutApiLoop, timeoutClearApiLoop] = useTimeout(wsNotReadyApiLoop, { ms: 300 });

  const wsCallback = useCallback((records) => {
    // timeout.current && clearTimeout(timeout.current);
    timeoutClearApiLoop();
    // const newAry = [...resData];
    const newAry = [...store.trade.tickers];
    records.map((obj) => {
      const index = newAry.findIndex((doc) => doc.s === obj.s);
      if (index < 0) newAry.push(obj);
      else newAry.splice(index, 1, obj);
    });
    // setResData(newAry);
    store.trade.updateState({ tickers: newAry });
    // console.log("wsCallback", records, store.trade.tickers);
  }, []);

  const isSearch = useMemo(() => !!keyword, [keyword]);
  const doSortArray = useCallback(
    (records, sortBy) => {
      if (!config) return records;
      const isUserSort = !isSearch && tabCfg.key === "user";

      const newAry: TickerProps[] = (() => {
        if (!isUserSort) return [...records];

        const ary: TickerProps[] = [];
        symbolStar.map((symbol) => {
          const doc = records.find((obj) => obj.s === symbol);
          doc && ary.push(doc);
        });
        return ary;
      })();
      // console.log("doSortArray", sortBy, records[0] && records[0].s);

      newAry.sort((x, y) => {
        if (sortBy) {
          const ary = sortBy.split("_");
          if (ary[0] === "name") {
            if (ary[1] === "up") return strCompare(y.s, x.s);
            if (ary[1] === "down") return strCompare(x.s, y.s);
          }
          if (ary[0] === "price") {
            if (ary[1] === "up") return +(x.c || 0) - +(y.c || 0);
            if (ary[1] === "down") return +(y.c || 0) - +(x.c || 0);
          }
          if (ary[0] === "rate") {
            if (ary[1] === "up") return +(x.cr || 0) - +(y.cr || 0);
            if (ary[1] === "down") return +(y.cr || 0) - +(x.cr || 0);
          }
          if (ary[0] === "volume") {
            if (ary[1] === "up") return +(x.q || 0) - +(y.q || 0);
            if (ary[1] === "down") return +(y.q || 0) - +(x.q || 0);
          }
        }

        if (isUserSort) return 0;

        const s1 = config[x.s] ? +config[x.s].displayWeight || 0 : 0;
        const s2 = config[y.s] ? +config[y.s].displayWeight || 0 : 0;
        return s2 - s1;
      });

      return newAry;

      function strCompare(a, b) {
        a = a.toLowerCase();
        b = b.toLowerCase();
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
      }
    },
    [config, tabCfg, symbolStar, isSearch]
  );
  const filterKeyword = useCallback((records, keyword) => {
    return records.filter((item) => {
      const marketName = item.s;
      let str = keyword.replace(/\//g, "_");
      str = str.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");
      return new RegExp(str, "i").test(marketName);
    });
  }, []);
  const filterHidden = useCallback(
    (records, tabCfg) => {
      if (tabCfg.noHidden) return records;

      if (!config) return [];

      const retAry: TickerProps[] = [];
      records.map((item) => {
        const marketName = item.s;
        const theMarket = config[marketName]; //找到市场的对应配置
        if (!theMarket || !/^(FULL)$/.test(theMarket.displayLevel)) return;
        retAry.push(item);
      });

      return retAry;
    },
    [config]
  );
  const filterResData: TickerProps[] = useMemo(() => {
    if (!config) {
      // console.error("【logErr】市场配置不存在 config");
      return [];
    }

    if (isSearch) {
      //搜索在全部市场模式下进行
      const records: TickerProps[] = [];

      tickers.map((item) => {
        const symbol = item.s;
        
        // 如果不是 CFD 页面，过滤掉以 _cfd 结尾的交易对
        if (!isCfd && symbol.endsWith("_cfd")) return;
        
        // 如果是 CFD 页面，根据合约类型过滤
        if (isCfd) {
          // 必须是以 _cfd 结尾的交易对
          if (!symbol.toLowerCase().endsWith("_cfd")) return;
          
          const quoteCurrency = symbol.split("_")[1]?.toUpperCase();
          const fiatCurrencies = ["AED", "BRL", "SAR"];
          const isFiatSymbol = fiatCurrencies.includes(quoteCurrency);
          
          // 如果是法币本位合约，只保留法币交易对（且必须带 _cfd）
          if (isFiatContract && !isFiatSymbol) return;
          // 如果是U本位合约，过滤掉法币交易对
          if (!isFiatContract && isFiatSymbol) return;
        }
        
        const theMarket = config[symbol]; //找到市场的对应配置
        if (!theMarket || theMarket.state === "DELISTED" || !/^(FULL|SEARCH)$/.test(theMarket.displayLevel)) return; //剔除不存在或者隐藏
        records.push(item);
      });

      return records;
    }

    const { buyCoinAry } = tabCfg;

    const records: TickerProps[] = [];

    tickers.map((item) => {
      const symbol = item.s;

      // 如果不是 CFD 页面，过滤掉以 _cfd 结尾的交易对
      if (!isCfd && symbol.endsWith("_cfd")) return;

      // 如果是 CFD 页面，根据合约类型过滤
      if (isCfd) {
        // 必须是以 _cfd 结尾的交易对
        if (!symbol.toLowerCase().endsWith("_cfd")) return;
        
        const quoteCurrency = symbol.split("_")[1]?.toUpperCase();
        const fiatCurrencies = ["AED", "BRL", "SAR"];
        const isFiatSymbol = fiatCurrencies.includes(quoteCurrency);
        
        // 如果是法币本位合约，只保留法币交易对（且必须带 _cfd）
        if (isFiatContract && !isFiatSymbol) return;
        // 如果是U本位合约，过滤掉法币交易对
        if (!isFiatContract && isFiatSymbol) return;
      }

      const buyCoinName = symbol.split("_")[1];
      if (buyCoinAry && !buyCoinAry.includes(buyCoinName)) return; //usds分组下，剔除不属于的市场

      if (tabCfg.key === "user" && !symbolStar.find((obj) => obj === symbol)) return;

      const theMarket = config[symbol]; //找到市场的对应配置
      if (!theMarket || theMarket.state === "DELISTED" || !/^(FULL|SEARCH)$/.test(theMarket.displayLevel)) return; //剔除不存在或者隐藏

      if (tabCfg.key === "etf" && !store.market.isEtfSymbolFn(theMarket)) return;

      if (tabCfg.key === "zone") {
        //板块模式下，剔除不匹配的数据
        const { plates } = theMarket;
        if (!plates || !plates.length) return;
        if (tabCfg.plateId && !plates.includes(tabCfg.plateId)) return;
      }

      // Crypto(usds)：排除大宗商品(260)、股票(261)，只展示加密货币
      if (tabCfg.key === "usds") {
        const plates = Array.isArray(theMarket.plates) ? theMarket.plates : [];
        if (plates.includes(260) || plates.includes(261)) return;
      }

      // 法币 tab(fiat 合并 aed/brl/sar)：排除大宗商品(260)、股票(261)，只展示该法币下的加密货币对
      const fiatTabKeys = ["aed", "brl", "sar", "fiat"];
      if (fiatTabKeys.includes(tabCfg.key)) {
        const plates = Array.isArray(theMarket.plates) ? theMarket.plates : [];
        if (plates.includes(260) || plates.includes(261)) return;
        // Crypto 法币对：按 账户充值法币、IP、余额 命中的法币过滤；都没命中(allowedFiatCodes 未加载或全量)则全展示
        if (isCfd && allowedFiatCodes != null && allowedFiatCodes.length > 0) {
          const quoteLower = buyCoinName.toLowerCase();
          if (!allowedFiatCodes.includes(quoteLower)) return;
        }
      }

      // 大宗商品 / 股票：按 plates 过滤（与 ssr-web-index market 页一致）
      if (tabCfg.key === "commodities") {
        const plates = Array.isArray(theMarket.plates) ? theMarket.plates : [];
        if (!plates.includes(260)) return;
        // 法币合约下的大宗商品法币对：同样按允许法币过滤
        if (isCfd && isFiatContract && allowedFiatCodes != null && allowedFiatCodes.length > 0) {
          const quoteLower = buyCoinName.toLowerCase();
          const isFiatQuote = ["aed", "brl", "sar"].includes(quoteLower);
          if (isFiatQuote && !allowedFiatCodes.includes(quoteLower)) return;
        }
      }
      if (tabCfg.key === "stocks") {
        const plates = Array.isArray(theMarket.plates) ? theMarket.plates : [];
        if (!plates.includes(261)) return;
        // 法币合约下的股票法币对：同样按允许法币过滤
        if (isCfd && isFiatContract && allowedFiatCodes != null && allowedFiatCodes.length > 0) {
          const quoteLower = buyCoinName.toLowerCase();
          const isFiatQuote = ["aed", "brl", "sar"].includes(quoteLower);
          if (isFiatQuote && !allowedFiatCodes.includes(quoteLower)) return;
        }
      }

      records.push(item);
    });

    return records;
  }, [tickers, config, tabCfg, symbolStar, isSearch, isCfd, isFiatContract, allowedFiatCodes]);
  const records: TickerProps[] = useMemo(() => {
    const sortAry = doSortArray(filterResData, sortBy);
    if (keyword) return filterKeyword(sortAry, keyword);
    return filterHidden(sortAry, tabCfg);
  }, [keyword, sortBy, tabCfg, filterResData, doSortArray, filterKeyword, filterHidden]);

  useEffect(() => {
    if (!config) return;
    // console.log("=====Config change");
    apiReqTicker24h(() => {
      setLoading(false);
      // setResData(ary);
    });
  }, [config]);
  useEffect(() => {
    if (loading) return;
    const msg = { event: `tickers` };
    Socket.addChannel(msg, wsCallback);
    timeoutApiLoop();
    // timeout.current && clearTimeout(timeout.current);
    // timeout.current = window.setTimeout(() => {
    //   !Socket.isReady && apiReqTicker24hLoop();
    // }, 300);

    return () => {
      Socket.removeChannel(msg);
      timeoutClearApiLoop();
      // timeout.current && clearTimeout(timeout.current);
    };
  }, [loading, timeoutApiLoop]);
  useEffect(() => {
    if (tabCfg.key === "user") store.user.getSymbolStar();
  }, [tabCfg]);
  useEffect(() => {
    isLogin && store.user.getSymbolStar();
  }, [isLogin]);

  return (
    <>
      {loading && <XtLoading className={styles.loading} />}
      {!loading && !records.length && <AppDivNoData />}
      {!loading && !!records.length && (
        <XtScrollWindow className={cx(styles.main)} height={30} dataAry={records}>
          <WindowUl isShowVolume={isShowVolume} onMarketSelect={onMarketSelect} />
        </XtScrollWindow>
      )}
    </>
  );
};

export default observer(Main);
// export default Main;
